import type { CSSProperties } from 'react';
import styled from 'styled-components';
import {
  smoothScrolling,
  touchOptimized,
  breakpoints
} from '../../../../styles/responsive';

interface NavigationWrapperProps {
  $openType: 'fullscreen' | 'ingame';
  $hidden?: boolean;
}

// Wrapper for the entire navigation area. The shown/hidden transform goes in as a custom property: as an
// interpolation, the first hide (on the first scroll down) generated a new class mid-scroll, and inserting its rule
// made Chromium re-match the whole styled-components sheet against the document.
export const NavigationWrapper = styled.div.attrs<NavigationWrapperProps>(({ $hidden, $openType, theme, style }) => {
  const wrapperStyle: CSSProperties & { '--navigation-wrapper-transform': string } = {
    ...style,
    '--navigation-wrapper-transform': $hidden
      ? `translateY(calc(100% + ${$openType === 'ingame' ? '0px' : theme.layout.statusBarHeight})) translateZ(0)`
      : 'translateY(0) translateZ(0)',
  };
  return { style: wrapperStyle };
})<NavigationWrapperProps>`
  position: ${({ $openType }) => $openType === 'ingame' ? 'absolute' : 'fixed'};
  bottom: ${({ $openType, theme }) => $openType === 'ingame' ? '0' : `${theme.layout.statusBarHeight}`};
  left: 0;
  right: 0;
  width: 100%;
  z-index: 90;
  display: flex;
  flex-direction: column;
  /* Hiding must travel own height PLUS the StatusBar strip below (fullscreen sits at
     bottom: statusBarHeight) — translateY(100%) alone leaves a bar-height sliver
     on screen where the StatusBar used to be. */
  transform: var(--navigation-wrapper-transform);
  transition: transform 0.5s ease;
  backface-visibility: hidden;
`;

export const NavigationContainer = styled.div<{ $openType: 'fullscreen' | 'ingame' }>`
  background: var(--navigation-background, #323334);
  transition: background-color 0.9s ease;
  box-shadow: 0px -7px 19px rgba(0, 0, 0, 0.23);
  display: flex;
  align-items: stretch;
  position: relative;
  min-height: 64px;
  ${({ $openType }) => $openType === 'ingame' && `border-radius: 0 0 10px 10px;`}

  ${breakpoints.mdUp(`min-height: 54px;`)}
  ${breakpoints.landscape(`min-height: 55px;`)}
  ${touchOptimized}
`;

export const LobbyTabContainer = styled.div`
  display: flex;
  box-shadow: 3px 0 8px 0 rgba(0, 0, 0, 0.30);

  /* Override pointer-events only for TabItem within LobbyTabContainer */
  > div[data-lobby-tab="true"] {
    pointer-events: auto;
  }
`
// Trigger chromatic build 
export const ScrollableTabsContainer = styled.div`
  position: relative;
  overflow-x: auto;
`

export const TabsContainer = styled.div<{ $openType: 'fullscreen' | 'ingame', $show: boolean }>`
  display: flex;
  height: 100%;
  flex: 1;
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  ${smoothScrolling}
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;

    ${({ $openType, $show }) =>
    $openType === 'ingame' && $show &&
    `
      @media (max-width: 1024px) {
        margin-right: 10px;
      }
    `}
`;

export const TabItem = styled.div<{ $isActive: boolean; $isDisabled?: boolean }>`
  flex: 0 0 auto;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  cursor: ${({ $isDisabled }) => $isDisabled ? 'not-allowed' : 'pointer'};
  position: relative;
  transition: all 0.2s ease;
  opacity: ${({ $isDisabled }) => $isDisabled ? 0.6 : 1};
  scroll-snap-align: center;
  ${touchOptimized}
  width: max-content;
  min-width: 60px;
  gap: 3px;
  padding: 4px;

  ${breakpoints.mdUp(`
    flex-direction: row;
    gap: 0;
    min-height: 64px;
    padding: 10px 5px;
  `)}

  ${breakpoints.landscape(`
    min-height: 55px;
    flex-direction: row;
  `)}

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: var(--navigation-active-color, ${({ theme }) => theme.colors.piliotGreen});
    box-shadow: 0 0 12px var(--navigation-active-glow, transparent);
    transform-origin: center;
    transform: scaleX(${({ $isActive }) => $isActive ? 1 : 0});
    ${({ $isActive }) => $isActive ? 'transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);' : ''}

    ${breakpoints.xs4(`height: 3px;`)}
  }
`;

export const TabIcon = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  img, svg {
    height: 27px;
    width: auto;
    margin: 0 4px 0 5px;
    transition: transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    ${breakpoints.mdUp(`
      height: 30px;
      margin: 0;
      padding-bottom: 3px;
    `)}
    ${breakpoints.landscape(`height: 30px;`)}
    @media (min-width: 769px) and (orientation: landscape) {
      padding-bottom: 0;
    }
  }
`;

export const TabLabel = styled.span<{ $isActive: boolean }>`
  text-align: center;
  color: ${({ $isActive, theme }) =>
    $isActive ? `var(--navigation-active-color, ${theme.colors.piliotGreen})` : '#ffffff'};
  text-shadow: ${({ $isActive }) => ($isActive ? '0 0 10px var(--navigation-active-glow, transparent)' : 'none')};
  transition: color 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-size: 12px;
  line-height: 12.8px;

  ${breakpoints.mdUp(`
    font-size: 14px;
    line-height: 16px;
    margin: 0 9px;
  `)}
  ${breakpoints.lgUp(`
    font-size: 16px;
    margin: 0 12px;
  `)}
`;
